/**
 * Reports Tabs Component
 *
 * Tabbed navigation for different report sections
 */

import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { OperationsData } from "../types/operations-types";
import type { OverviewData } from "../types/overview-types";
import type { WebData } from "../types/web-types";
import { BookingSummary } from "./booking-summary";
import { CustomerSummary } from "./customer-summary";
import { DiscountTab } from "./discounts/discount-tab";
import { FinancialSummary } from "./financial-summary";
import { OperationsSummary } from "./operations-summary";
import { WebSummary } from "./web-summary";

interface ReportsTabsProps {
  overviewData: OverviewData;
  operationsData: OperationsData | null;
  webData: WebData | null;
}

export function ReportsTabs({ overviewData, operationsData, webData }: ReportsTabsProps) {
  const { t } = useTranslation("reports");

  return (
    <Tabs defaultValue="financial" className="w-full">
      <div className="-mx-4 md:mx-0 overflow-x-auto md:overflow-visible [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
        <TabsList className="flex w-max h-auto md:h-9 p-1.5 md:p-1 md:grid md:w-full md:grid-cols-5 mx-4 md:mx-0">
          <TabsTrigger
            value="financial"
            className="flex-shrink-0 px-5 md:px-3 py-2.5 md:py-1 text-base md:text-sm"
          >
            {t("tabs.financial")}
          </TabsTrigger>
          <TabsTrigger
            value="customers"
            className="flex-shrink-0 px-5 md:px-3 py-2.5 md:py-1 text-base md:text-sm"
          >
            {t("tabs.customers")}
          </TabsTrigger>
          <TabsTrigger
            value="operations"
            className="flex-shrink-0 px-5 md:px-3 py-2.5 md:py-1 text-base md:text-sm"
          >
            {t("tabs.operations")}
          </TabsTrigger>
          <TabsTrigger
            value="web"
            className="flex-shrink-0 px-5 md:px-3 py-2.5 md:py-1 text-base md:text-sm"
          >
            {t("tabs.web")}
          </TabsTrigger>
          <TabsTrigger
            value="discounts"
            className="flex-shrink-0 px-5 md:px-3 py-2.5 md:py-1 text-base md:text-sm"
          >
            {t("tabs.discounts")}
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="financial" className="space-y-4 mt-6">
        <FinancialSummary data={overviewData.financial} />
      </TabsContent>

      <TabsContent value="customers" className="space-y-4 mt-6">
        <CustomerSummary data={overviewData.customers} />
      </TabsContent>

      <TabsContent value="operations" className="space-y-4 mt-6">
        {/* Bookings Section - Always available */}
        <BookingSummary data={overviewData.bookings} />

        {/* Operations Section - Shows mocked/blurred data for basic tier */}
        <OperationsSummary data={operationsData?.operations ?? null} />
      </TabsContent>

      <TabsContent value="web" className="space-y-4 mt-6">
        <WebSummary data={webData?.web ?? null} />
      </TabsContent>

      <TabsContent value="discounts" className="space-y-4 mt-6">
        <DiscountTab />
      </TabsContent>
    </Tabs>
  );
}
